import React, { useEffect, useState } from 'react';
import i18next from 'i18next';
import { useSelector, useDispatch } from 'react-redux';

import './TopMenu.scss';
import { UserMenu, UserMenuConfig } from './UserMenu/UserMenu';
import { userMenuConfig } from './TopMenuConfiguration';
import { getNewNotificationsSectionsWithTag } from '../../../../../helpers/TagsHelper';

import Gauges from '../../../../components/Gauges/Gauges';
import { SgLogo } from '../../../../components/SgLogo/SgLogo';
import { BurgerIcon } from '../../../../components/BurgerIcon/BurgerIcon';
import { selectNotificationsData } from '../../../../store/User/reducer';
import { storeOnboardingData } from '../../../../store/User/actions/storeOnboardingData';

import { OnboardingNotifications } from '../../../../../models/OnboardingNotifications';
import { User } from '../../../../../models/User';
import { ActiveProduct } from '../../../../../models/ActiveProduct';
import { Branding } from '../../../../../models/Branding';
import { selectBranding } from '../../../../../main/store/Branding/reducer';
import { ExtendedProducts } from '../../../../../models/ExtendedProducts';
import { SIDE_MENU_URL_LOGO } from '../../SideNavigationMenu/SideNavigationMenuConfiguration';
import { HelpCenterLanguages } from '../../../../constants/Languages';

export interface SGTopMenuProps {
    clickAction: any;
    user: User;
    activeProducts: ActiveProduct[];
    extendedProducts: ExtendedProducts;
    permissions: string[];
}

export const TopMenu = ({ clickAction, user, activeProducts, extendedProducts, permissions }: SGTopMenuProps) => {
    const [logoUrl, setLogoUrl] = useState<string>('');
    const branding: InstanceType<typeof Branding> | undefined = useSelector(selectBranding);

    useEffect(() => {
        const brandingBlueLogo = branding?.secondaryLogo;
        const signaturitBlueLogo = SIDE_MENU_URL_LOGO.BLUE_LINK;
        const srcLogo = brandingBlueLogo ?? signaturitBlueLogo;
        setLogoUrl(srcLogo);
    }, [branding]);

    const dispatch = useDispatch();

    const onboardingNotifications: OnboardingNotifications | undefined = useSelector(selectNotificationsData);

    const [userMenuItems, setUserMenuItems] = useState<UserMenuConfig[]>([]);

    const isTrialActive = user?.isTrial;
    const hiddenSections = branding?.hiddenSections;

    useEffect(() => {
        if (user && onboardingNotifications) {
            const sectionsWithTag = getNewNotificationsSectionsWithTag(user, extendedProducts);
            const sectionsWithTagToDisplay = sectionsWithTag.filter((section) =>
                onboardingNotifications.isTemporalNotificationVisible(section)
            );

            setUserMenuItems(
                userMenuConfig({ user, activeProducts, hiddenSections, permissions }, sectionsWithTagToDisplay)
            );

            dispatch(storeOnboardingData(onboardingNotifications));
        }
    }, [user, onboardingNotifications]);

    const languageToUrlMap: Record<HelpCenterLanguages, string> = {
        en: 'https://help.signaturit.com/hc/en-us/categories/20917081662865-Signaturit',
        es: 'https://help.signaturit.com/hc/es/categories/20917081662865-Signaturit',
        fr: 'https://help.signaturit.com/hc/fr/categories/20917081662865-Signaturit',
    };

    const helpURL =
        languageToUrlMap[i18next.language as HelpCenterLanguages] || languageToUrlMap['en' as HelpCenterLanguages];

    return (
        <div className={`top-menu__container ${isTrialActive ? ' trialActive' : ''}`}>
            <div className="top-menu__left">
                <div className="top-menu__icon-wrapper" onClick={clickAction}>
                    <BurgerIcon />
                </div>
                <div className="top-menu__logo-wrapper" data-signa="top-menu-logo-wrapper">
                    <SgLogo href="#" src={logoUrl} />
                </div>
            </div>
            <div className="top-menu__gauges-wrapper">
                <Gauges />
            </div>
            <div className="top-menu__help-wrapper">
                <a data-signa="help-link" target="_blank" href={helpURL}>
                    {i18next.t('default.menus._top.help')}
                </a>
            </div>
            <div className={`top-menu__user-section${isTrialActive ? ' trialActive' : ''}`}>
                <UserMenu user={user} config={userMenuItems} />
            </div>
        </div>
    );
};
